import { locale } from '../lib/locale';

/**
 * Larovi Plus (1.21): status in the settings, the calm hint at a limit, the small "Plus" badge.
 * Glossary: Larovi Plus stays the product name in English; Probemonat = free trial.
 * German is the master.
 */
export const PLUS_TEXT = {
  de: {
    section: { title: 'Larovi Plus', hint: 'Euer Status und was dazugehört' },
    // the closed section already says where the family stands (Mike, 16.10.)
    sectionHint: {
      free: 'Kostenlos',
      trialOver: 'Kostenlos · Probemonat vorbei',
      trial: (days: number) =>
        days === 1 ? 'Probemonat · noch 1 Tag' : `Probemonat · noch ${days} Tage`,
      plus: 'Plus ist aktiv',
      testfamily: 'Testfamilie · alles frei',
    },
    badge: 'Plus',
    statusTitle: 'Euer Status',
    status: {
      free: 'Kostenlos',
      trialOver: 'Kostenlos · Probemonat vorbei',
      trial: (days: number) =>
        days === 1 ? 'Probemonat · noch 1 Tag' : `Probemonat · noch ${days} Tage`,
      plus: 'Larovi Plus',
      testfamily: 'Testfamilie',
    },
    statusText: {
      free: 'Ihr nutzt Larovi kostenlos. Alles, was ihr kostenlos nutzt, bleibt kostenlos.',
      trialOver:
        'Euer Probemonat ist vorbei. Alles, was ihr angelegt habt, bleibt – auch Rezepte über 20. Neue Rezepte darüber, der Essensplan und Rezepte per Screenshot gehen mit Plus.',
      trial:
        'Ihr nutzt alles von Plus, ohne Zahlungsdaten. Danach geht ihr einfach kostenlos weiter – nichts geht verloren.',
      plus: 'Ihr habt alles von Plus. Danke, dass ihr Larovi möglich macht.',
      testfamily: 'Als Testfamilie nutzt ihr alles kostenlos, solange der Test läuft.',
    },
    // only what exists today (Mike, 16.10.: Erinnerungen gibt es noch nicht – nicht nennen)
    partsTitle: 'Das gehört zu Plus',
    parts: [
      { title: 'Rezepte ohne Grenze', text: 'Kostenlos sind 20 Rezepte, mit Plus beliebig viele.' },
      {
        title: 'Essensplan für die Woche',
        text: '„Heute gibt’s“ auf dem Handy und an der Wand, die Kinder stimmen ab, die Zutaten der Woche gebündelt auf die Liste.',
      },
      {
        title: 'Rezepte aus Screenshot oder Foto',
        text: 'Larovi liest Zutaten und Schritte für euch – und räumt Rezept-Links ohne lesbares Rezept auf.',
      },
    ],
    soonTitle: 'Kommt dazu',
    soon: '„Was essen wir heute?“ – Vorschläge aus euren Rezepten, auch für offene Tage im Plan.',
    freeStays:
      'Was ihr kostenlos nutzt, bleibt kostenlos – auch die Einkaufsliste und eure ersten 20 Rezepte.',
    buySoon: 'Plus buchen geht bald direkt hier in der App.',
    trialEnding: (days: number) =>
      days === 1
        ? 'Euer Probemonat endet morgen. Nichts geht verloren – mehr dazu unter Larovi Plus.'
        : `Euer Probemonat endet in ${days} Tagen. Nichts geht verloren – mehr dazu unter Larovi Plus.`,
    more: 'Mehr über Larovi Plus',
    notNow: 'Jetzt nicht',
    recipesFullTitle: 'Eure 20 Rezepte sind voll',
    recipesFullText:
      'Alle 20 bleiben, ihr könnt sie weiter kochen und ändern. Für mehr Rezepte gibt es Larovi Plus – dazu Essensplan, Rezepte per Screenshot und bald Vorschläge.',
    recipesFullTip: 'Tipp: Ein Rezept löschen schafft Platz für ein neues.',
    plusOnlyTitle: 'Das gehört zu Larovi Plus',
    plusOnlyText:
      'Rezepte aus Screenshot oder Foto liest Larovi mit KI. Per Link oder von Hand geht es kostenlos.',
  },
  en: {
    section: { title: 'Larovi Plus', hint: 'Your status and what is included' },
    sectionHint: {
      free: 'Free',
      trialOver: 'Free · trial over',
      trial: (days: number) =>
        days === 1 ? 'Free trial · 1 day left' : `Free trial · ${days} days left`,
      plus: 'Plus is active',
      testfamily: 'Pilot family · everything free',
    },
    badge: 'Plus',
    statusTitle: 'Your status',
    status: {
      free: 'Free',
      trialOver: 'Free · trial over',
      trial: (days: number) =>
        days === 1 ? 'Free trial · 1 day left' : `Free trial · ${days} days left`,
      plus: 'Larovi Plus',
      testfamily: 'Pilot family',
    },
    statusText: {
      free: 'You are using Larovi for free. Everything you use for free stays free.',
      trialOver:
        'Your free trial is over. Everything you created stays – including recipes beyond 20. New recipes beyond that, the meal plan and recipes from a screenshot work with Plus.',
      trial:
        'You can use everything in Plus, no payment details needed. Afterwards you simply carry on for free – nothing is lost.',
      plus: 'You have everything in Plus. Thank you for making Larovi possible.',
      testfamily: 'As a pilot family you can use everything for free while the pilot runs.',
    },
    partsTitle: 'What Plus includes',
    parts: [
      { title: 'Unlimited recipes', text: '20 recipes are free, with Plus as many as you like.' },
      {
        title: 'Meal plan for the week',
        text: '“Today we’re having” on your phone and on the wall, the kids vote, the week’s ingredients bundled onto the list.',
      },
      {
        title: 'Recipes from a screenshot or photo',
        text: 'Larovi reads the ingredients and steps for you – and tidies up recipe links without a readable recipe.',
      },
    ],
    soonTitle: 'Coming soon',
    soon: '“What shall we eat today?” – suggestions from your recipes, for open days in the plan too.',
    freeStays:
      'Everything you use for free stays free – including the shopping list and your first 20 recipes.',
    buySoon: 'You will soon be able to get Plus right here in the app.',
    trialEnding: (days: number) =>
      days === 1
        ? 'Your free trial ends tomorrow. Nothing is lost – more under Larovi Plus.'
        : `Your free trial ends in ${days} days. Nothing is lost – more under Larovi Plus.`,
    more: 'More about Larovi Plus',
    notNow: 'Not now',
    recipesFullTitle: 'Your 20 recipes are full',
    recipesFullText:
      'All 20 stay – you can keep cooking and changing them. For more recipes there is Larovi Plus, with the meal plan, recipes from a screenshot and, soon, suggestions.',
    recipesFullTip: 'Tip: deleting a recipe makes room for a new one.',
    plusOnlyTitle: 'This is part of Larovi Plus',
    plusOnlyText:
      'Larovi reads recipes from a screenshot or photo with AI. Adding them by link or by hand is free.',
  },
};

export const plusText = PLUS_TEXT[locale];
